if (typeof customElements.get('bundle-cart') === 'undefined') {
  class BundleCart extends HTMLElement {
    constructor() {
      super();
      this.lastValid = null;
      this.lines = [];
      this.repairing = false;
      this.timer = null;
    }

    connectedCallback() {
      this.cartUrl = this.dataset.cartUrl || '/cart';

      this.patchFetch();
      document.addEventListener('cart-updated', () => this.schedule(), true);

      this.observer = new MutationObserver(() => this.decorate());

      if (this.dataset.hasBundle === 'true' && document.getElementById('AjaxCartForm')) {
        this.sync();
      }
    }

    patchFetch() {
      const original = window.fetch;
      const self = this;

      window.fetch = function bundleCartFetch(...args) {
        const input = args[0];
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const result = original.apply(this, args);

        if (/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url)) {
          result.then(() => self.schedule()).catch(() => {});
        }

        return result;
      };
    }

    schedule() {
      if (this.repairing) return;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.sync(), 250);
    }

    async sync() {
      let cart = null;

      try {
        const response = await fetch(`${this.cartUrl}.js`, {
          headers: { Accept: 'application/json' }
        });
        cart = await response.json();
      } catch (error) {
        return;
      }

      this.lines = (cart.items || []).map((item, index) => ({
        key: item.key,
        line: index + 1,
        id: item.variant_id,
        quantity: item.quantity,
        properties: item.properties || {},
        bundleId: (item.properties || {})._bundle_id || null,
        sizes: (item.properties || {})._bundle_sizes || ''
      }));

      const groups = this.groups();
      const invalid = [];

      groups.forEach((group) => {
        if (!this.allowedSizes(group).includes(group.total)) {
          invalid.push(group);
        }
      });

      if (invalid.length > 0 && this.lastValid && this.attempts() < 3) {
        await this.repair(invalid);
        return;
      }

      if (invalid.length > 0) {
        const group = invalid[0];
        this.notify(
          this.dataset.localeInvalid
            .replace('[count]', group.total)
            .replace('[sizes]', this.allowedSizes(group).join(', ')),
          true
        );
      } else {
        this.notify('');
        this.setAttempts(0);
        this.lastValid = new Map(
          this.lines.filter((line) => line.bundleId).map((line) => [line.key, line])
        );
      }

      this.decorate();
    }

    groups() {
      const map = new Map();

      this.lines.forEach((line) => {
        if (!line.bundleId) return;
        if (!map.has(line.bundleId)) {
          map.set(line.bundleId, { id: line.bundleId, lines: [], total: 0, sizes: line.sizes });
        }
        const group = map.get(line.bundleId);
        group.lines.push(line);
        group.total += line.quantity;
      });

      return Array.from(map.values());
    }

    allowedSizes(group) {
      return group.sizes
        .split(',')
        .map((size) => parseInt(size, 10))
        .filter((size) => size > 0);
    }

    async repair(groups) {
      this.repairing = true;

      const updates = {};
      const additions = [];

      groups.forEach((group) => {
        const restored = [];

        this.lastValid.forEach((line) => {
          if (line.bundleId === group.id) {
            restored.push(line);
          }
        });

        if (restored.length === 0) return;

        restored.forEach((line) => {
          const present = group.lines.some((current) => current.key === line.key);
          if (present) {
            updates[line.key] = line.quantity;
          } else {
            additions.push({ id: line.id, quantity: line.quantity, properties: line.properties });
          }
        });

        group.lines.forEach((line) => {
          const known = Array.from(this.lastValid.values()).some(
            (valid) => valid.key === line.key && valid.bundleId === group.id
          );
          if (!known) {
            updates[line.key] = 0;
          }
        });
      });

      if (Object.keys(updates).length === 0 && additions.length === 0) {
        this.repairing = false;
        const group = groups[0];
        this.notify(
          this.dataset.localeInvalid
            .replace('[count]', group.total)
            .replace('[sizes]', this.allowedSizes(group).join(', ')),
          true
        );
        this.decorate();
        return;
      }

      this.setAttempts(this.attempts() + 1);

      try {
        if (Object.keys(updates).length > 0) {
          await fetch(`${this.cartUrl}/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates })
          });
        }

        if (additions.length > 0) {
          await fetch(`${this.cartUrl}/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: additions })
          });
        }

        const sizes = this.allowedSizes(groups[0]).join(', ');
        this.pendingNotice = this.dataset.localeRestored.replace('[sizes]', sizes);
        this.refresh();
      } catch (error) {
        this.notify(this.dataset.localeError, true);
      } finally {
        this.repairing = false;
      }
    }

    attempts() {
      try {
        return parseInt(window.sessionStorage.getItem('bundle-cart-repairs'), 10) || 0;
      } catch (error) {
        return 0;
      }
    }

    setAttempts(value) {
      try {
        window.sessionStorage.setItem('bundle-cart-repairs', value);
      } catch (error) {
        return;
      }
    }

    refresh() {
      if (typeof window.refreshCart === 'function') {
        window.refreshCart();
        setTimeout(() => {
          this.sync().then(() => {
            if (this.pendingNotice) {
              this.notify(this.pendingNotice, true);
              this.pendingNotice = null;
            }
          });
        }, 400);
      } else {
        window.location.reload();
      }
    }

    decorate() {
      const holders = document.querySelectorAll('#AjaxCartForm');
      if (holders.length === 0) return;

      this.observer.disconnect();

      const byKey = new Map(this.lines.map((line) => [line.key, line]));
      const groups = this.groups();

      document.querySelectorAll('.bundle-cart-group').forEach((element) => element.remove());

      document.querySelectorAll('[data-js-cart-item]').forEach((element) => {
        const line = byKey.get(element.dataset.id);
        const isBundle = Boolean(line && line.bundleId);
        element.classList.toggle('cart-item--bundle', isBundle);
        if (isBundle) {
          element.dataset.bundleId = line.bundleId;
        } else {
          delete element.dataset.bundleId;
        }
      });

      groups.forEach((group) => {
        const elements = Array.from(document.querySelectorAll('[data-js-cart-item]')).filter(
          (element) => element.dataset.bundleId === group.id
        );
        if (elements.length === 0) return;

        const last = elements[elements.length - 1];
        const footer = document.createElement('div');
        footer.classList.add('bundle-cart-group');

        const label = document.createElement('span');
        label.classList.add('bundle-cart-group__label');
        label.textContent = this.dataset.localeGroup.replace('[count]', group.total);
        footer.appendChild(label);

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.classList.add('bundle-cart-group__remove');
        remove.textContent = this.dataset.localeRemoveBundle;
        remove.addEventListener('click', () => this.removeBundle(group));
        footer.appendChild(remove);

        last.insertAdjacentElement('afterend', footer);
      });

      holders.forEach((holder) => {
        this.observer.observe(holder, { childList: true, subtree: true });
      });
    }

    async removeBundle(group) {
      this.repairing = true;

      const updates = {};
      group.lines.forEach((line) => {
        updates[line.key] = 0;
      });

      try {
        await fetch(`${this.cartUrl}/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates })
        });
        this.lastValid = null;
        this.refresh();
      } catch (error) {
        this.notify(this.dataset.localeError, true);
      } finally {
        this.repairing = false;
      }
    }

    notify(text, isError) {
      const holder = document.getElementById('AjaxCartForm');
      if (!holder) return;

      let notice = holder.querySelector('.bundle-cart-notice');

      if (!text) {
        if (notice) notice.remove();
        return;
      }

      if (!notice) {
        notice = document.createElement('p');
        notice.classList.add('bundle-cart-notice');
        notice.setAttribute('role', 'status');
        holder.prepend(notice);
      }

      notice.textContent = text;
      notice.classList.toggle('bundle-cart-notice--error', Boolean(isError));
    }
  }

  customElements.define('bundle-cart', BundleCart);
}
